import { ReactNode, useState } from "react";
import {
  Box,
  Collapse,
  IconButton,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import SuccessIcon from "@icons/myicons/check-circle-filled.svg?react";
import ErrorIcon from "@icons/myicons/error-filled.svg?react";
import ArrowDownIcon from "@icons/myicons/arrow-down.svg?react";
import { CloseButton } from "@rentablo/components";

const StyledArrowDownIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isCollapsed",
})<{ isCollapsed: boolean }>(({ isCollapsed }) => ({
  display: "flex",
  alignItems: "center",
  transform: `rotate(${isCollapsed ? "0" : "180"}deg)`,
  transition: "transform 0.2s ease-in-out",
  transformOrigin: "center",
}));

export type StatusMessageVariant = "success" | "error";

export interface StatusMessageProps {
  variant: StatusMessageVariant;
  title: ReactNode;
  onClose: () => void;
  children?: ReactNode;
  collapsible?: boolean;
  "data-testid"?: string;
}

export const StatusMessage = ({
  variant,
  title,
  onClose,
  children,
  collapsible = children != null,
  ...rest
}: StatusMessageProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const theme = useTheme();

  return (
    <Box {...rest}>
      <Box display="flex" justifyContent="space-between" gap={3}>
        <Box display="flex" gap={1.5} alignItems="center">
          {variant === "success" ? (
            <SuccessIcon
              color={theme.palette.icon.success}
              width={28}
              height={28}
            />
          ) : (
            <ErrorIcon
              color={theme.palette.icon.error}
              width={28}
              height={28}
            />
          )}
          <Typography
            variant="body3"
            color={variant === "success" ? "primary.main" : "textSecondary"}
          >
            {title}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {collapsible && (
            <IconButton size="small" onClick={() => setIsOpen((open) => !open)}>
              <StyledArrowDownIcon isCollapsed={!isOpen}>
                <ArrowDownIcon />
              </StyledArrowDownIcon>
            </IconButton>
          )}
          <CloseButton onClose={onClose} size="small" />
        </Box>
      </Box>
      {collapsible && <Collapse in={isOpen}>{children}</Collapse>}
    </Box>
  );
};
